export type GeographyType = 'us_state' | 'country';

export interface State {
  id: number;
  name: string;
  capital: string;
  region: string;
  abbreviation: string;
  geography_type?: GeographyType;
  iso_code?: string;
  continent?: string;
  largest_city?: string;
  clue_1?: string;
  clue_2?: string;
  clue_3?: string;
  flag_emoji?: string;
  population?: number;
  nickname?: string;
}

export interface GameState {
  id: number;
  name: string;
  capital: string;
  abbreviation: string;
  currentClue: string;
  clue_1?: string;
  clue_2?: string;
  clue_3?: string;
  clueLevel: number;
  attempts: number;
  hintUsed: boolean;
  completed: boolean;
  score: number;
}

export interface GameSession {
  sessionId: string;
  states: GameState[];
  currentStateIndex: number;
  totalScore: number;
  startTime: number;
  gameMode: string;
}

export type GameMode =
  // US States
  | 'full_usa'
  | 'northeast'
  | 'mid_atlantic'
  | 'southeast'
  | 'midwest'
  | 'southwest'
  | 'west'
  // Countries (continents + regions)
  | 'north_america'
  | 'caribbean'
  | 'south_america'
  | 'europe'
  | 'africa'
  | 'asia';

export type Difficulty = 'easy' | 'medium' | 'hard';

export const US_STATE_MODES: Record<string, string> = {
  full_usa: 'Full USA (50 States)',
  northeast: 'Northeast Region',
  mid_atlantic: 'Mid-Atlantic Region',
  southeast: 'Southeast Region',
  midwest: 'Midwest Region',
  southwest: 'Southwest Region',
  west: 'West Region'
};

export const COUNTRY_MODES: Record<string, string> = {
  north_america: 'North America',
  caribbean: 'Caribbean Islands',
  south_america: 'South America',
  europe: 'Europe',
  africa: 'Africa',
  asia: 'Asia'
};

export const GAME_MODES: Record<GameMode, string> = {
  ...US_STATE_MODES,
  ...COUNTRY_MODES
} as Record<GameMode, string>;

export const DIFFICULTIES: Record<Difficulty, { name: string; description: string }> = {
  easy: {
    name: 'Easy',
    description: 'Name shown, hints reveal capital and facts'
  },
  medium: {
    name: 'Medium',
    description: 'Capital name shown, hints reveal facts'
  },
  hard: {
    name: 'Hard',
    description: 'Only facts given as clues'
  }
};

// Helper functions
export function getGeographyType(gameMode: GameMode): GeographyType {
  return Object.keys(COUNTRY_MODES).includes(gameMode) ? 'country' : 'us_state';
}

export function isCountryMode(gameMode: GameMode): boolean {
  return Object.keys(COUNTRY_MODES).includes(gameMode);
}

export function isUSStateMode(gameMode: GameMode): boolean {
  return Object.keys(US_STATE_MODES).includes(gameMode);
}

export const SCORING = {
  CORRECT_FIRST_NO_HINT: 5,
  CORRECT_FIRST_WITH_HINT: 4,
  CORRECT_SECOND: 2,
  FAILED: 0
};
